import { useState, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { Card, Loader, Progress } from "@mantine/core";
import { HiExclamationCircle } from "react-icons/hi2";
import {
  useBulkValidationPoll,
  TIMEOUT_FALLBACK_MESSAGE,
} from "../hooks/useBulkValidationPoll";
import { useFileUploadFlow } from "../hooks/useFileUploadFlow";
import useFileUploadStore from "../../../../stores/fileUploadStore";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomLeaveConfirmationModal from "../../../ui/CustomLeaveConfirmationModal/CustomLeaveConfirmationModal";

const TICK_INTERVAL_MS = 300;
const FINISH_TICK_MS = 50;
const FINISH_DURATION_MS = 600;
const FINISH_TICKS = FINISH_DURATION_MS / FINISH_TICK_MS;

function useSimulatedProgress(active, { finishing = false, onFinish } = {}) {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const intervalRef = useRef(null);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    clearInterval(intervalRef.current);

    if (finishing) {
      const start = progressRef.current;
      const step = (100 - start) / FINISH_TICKS;
      let ticksElapsed = 0;
      intervalRef.current = setInterval(() => {
        ticksElapsed += 1;
        const next =
          ticksElapsed >= FINISH_TICKS ? 100 : start + step * ticksElapsed;
        setProgress(next);
        if (next >= 100) {
          clearInterval(intervalRef.current);
          onFinishRef.current?.();
        }
      }, FINISH_TICK_MS);
      return () => clearInterval(intervalRef.current);
    }

    if (!active) {
      setProgress(0);
      return;
    }

    setProgress(0);
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return 90;
        return prev + (90 - prev) * 0.05;
      });
    }, TICK_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [active, finishing]);

  return Math.round(progress);
}

function Step2Upload() {
  const { uiState, handleCancel } = useFileUploadFlow();
  const { isTimedOut, timeoutMessage, isFinishing, commitResult } =
    useBulkValidationPoll();

  const isUploading = uiState === "uploading";
  const isActive = isUploading || uiState === "validating";

  // Once timed out, "View Batch History" below is the sanctioned way to leave —
  // the guard must not also block that navigation.
  const isPolling = uiState === "validating";
  const shouldGuardLeave = isPolling && !isTimedOut;

  const blocker = useBlocker(shouldGuardLeave);

  useEffect(() => {
    if (!shouldGuardLeave) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuardLeave]);

  const progress = useSimulatedProgress(isActive && !isTimedOut, {
    finishing: isFinishing,
    onFinish: commitResult,
  });

  const leaveModal = (
    <CustomLeaveConfirmationModal
      opened={blocker.state === "blocked"}
      message="This batch isn't approved yet. Please stay until validation finishes and you've submitted it for approval — unlike manual entries, file upload batches require this step, and leaving now means you won't be able to come back and approve it later."
      onStay={() => blocker.reset()}
      onLeave={() => blocker.proceed()}
    />
  );

  if (uiState === "file-upload-failed") {
    return (
      <Card shadow="xs" radius="lg" p={{ base: "md", sm: "xl" }}>
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <HiExclamationCircle size={40} className="text-red-500" />
          <div className="flex flex-col gap-1">
            <p className="text-base font-bold text-gray-900">
              File Upload Failed
            </p>
            <p className="text-sm text-gray-400">
              We couldn&apos;t process your file due to a system error. No
              entries were saved! Please try uploading your file again.
            </p>
          </div>
          <CustomButton onClick={() => useFileUploadStore.getState().reset()}>
            Upload New File
          </CustomButton>
        </div>
      </Card>
    );
  }

  if (isTimedOut) {
    const hasError = timeoutMessage !== TIMEOUT_FALLBACK_MESSAGE;
    return (
      <>
        {leaveModal}
        <Card shadow="xs" radius="lg" p={{ base: "md", sm: "xl" }}>
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <HiExclamationCircle
              size={40}
              className={hasError ? "text-red-500" : "text-amber-500"}
            />
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-gray-900">
                {timeoutMessage}
              </p>
              <p className="text-sm text-gray-400">
                Your file was uploaded. Check batch history to track its status.
              </p>
            </div>
            <CustomButton onClick={handleCancel}>
              View Batch History
            </CustomButton>
          </div>
        </Card>
      </>
    );
  }

  const title = isUploading
    ? "Uploading your file..."
    : "Validating your file...";
  const subtitle = isUploading
    ? "Please wait while we upload your payment file"
    : "Please wait while we process your payment file";

  return (
    <>
      {leaveModal}
      <Card shadow="xs" radius="lg" p={{ base: "md", sm: "xl" }}>
        <div className="flex flex-col items-center gap-6 py-8">
          <Loader size="xl" color="var(--mantine-color-primary-6)" />

          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-base font-bold text-gray-900">{title}</p>
            <p className="text-sm text-gray-400">{subtitle}</p>
          </div>

          <div className="w-full max-w-sm flex flex-col gap-2">
            <Progress value={progress} radius="xl" />
            <p className="text-xs text-gray-500 text-center">
              {progress}% complete
            </p>
          </div>
        </div>
      </Card>
    </>
  );
}

export default Step2Upload;
